import { Link } from 'react-router-dom';
import { Building2, MapPin, Globe } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import type { Business } from '@/lib/api/businesses';

type Props = {
  business: Business;
};

export function BusinessCard({ business }: Props) {
  const location = [business.city, business.country].filter(Boolean).join(', ');

  return (
    <Link
      to={`/businesses/${business.slug}`}
      className="group flex flex-col gap-3 rounded-2xl border border-border/70 bg-white p-4 shadow-sm transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/40"
    >
      <div className="flex items-start gap-3">
        {business.logo_url ? (
          <img
            src={business.logo_url}
            alt=""
            className="h-12 w-12 shrink-0 rounded-lg object-cover"
          />
        ) : (
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-navy/5">
            <Building2 className="h-6 w-6 text-navy/30" aria-hidden="true" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-navy group-hover:text-navy-deep">
            {business.name}
          </p>
          {business.tagline && (
            <p className="mt-0.5 line-clamp-2 text-xs text-muted">{business.tagline}</p>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="gold">{business.category}</Badge>
        {business.verified && <Badge variant="verified">Verified</Badge>}
      </div>

      <div className="flex flex-col gap-1 text-xs text-muted">
        {location && (
          <span className="flex items-center gap-1">
            <MapPin className="h-3 w-3 shrink-0" aria-hidden="true" />
            {location}
          </span>
        )}
        {business.website && (
          <span className="flex items-center gap-1 truncate">
            <Globe className="h-3 w-3 shrink-0" aria-hidden="true" />
            {business.website.replace(/^https?:\/\//, '')}
          </span>
        )}
      </div>
    </Link>
  );
}
